// src/components/PromptAdmin.tsx
/**
 * Panel de Administración de Prompts — v2.0
 *
 * Permite a admin/dev/CTO:
 * - Editar prompt conciencia (solo CTO)
 * - Editar prompt base de la organización
 * - Editar/crear prompts comerciales
 * - Editar prompt por thread
 * - Crear nuevos threads con prompt personalizado (GPT-like)
 *
 * Jerarquía: conciencia → base → comercial → thread
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  FileText,
  Save,
  RotateCw,
  Lock,
  Unlock,
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Settings,
  Plus,
  Sparkles,
  Brain,
  Building2,
  ShoppingCart,
  MessageSquare,
  Eye,
  Edit3,
  Trash2,
  X,
  Package,
  Database,
  ToggleLeft,
  ToggleRight,
  BarChart3,
} from 'lucide-react';
import { ModuleAdmin } from './ModuleAdmin';
import { useModules } from '@/context/ModulesContext';
import {
  getPromptLayers,
  getConcienciaPrompt,
  updateConcienciaPrompt,
  getOrgBasePrompt,
  updateOrgBasePrompt,
  listComercialPrompts,
  createComercialPrompt,
  updateComercialPrompt,
  getThreads,
  getThreadPrompts,
  updateThreadPrompt,
  resetThreadPrompt,
  startThread,
  getCustomFieldAnalytics,
  type PromptLayerDTO,
  type ThreadDTO,
  type ResponseSchema,
  type ResponseSchemaField,
  type ContextConfig,
  type CustomFieldStats,
} from '@/lib/api';

/* ─── Types ─── */
type PromptTab = 'conciencia' | 'base' | 'comercial' | 'threads' | 'modules';

interface EditState {
  content: string;
  name: string;
  dirty: boolean;
}

export function PromptAdmin() {
  // Global
  const [activeTab, setActiveTab] = useState<PromptTab>('comercial');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Conciencia
  const [conciencia, setConciencia] = useState<PromptLayerDTO | null>(null);
  const [concienciaEdit, setConcienciaEdit] = useState<EditState>({ content: '', name: '', dirty: false });

  // Base
  const [base, setBase] = useState<PromptLayerDTO | null>(null);
  const [baseEdit, setBaseEdit] = useState<EditState>({ content: '', name: '', dirty: false });
  const [baseSubTab, setBaseSubTab] = useState<'prompt' | 'schema' | 'context' | 'analytics'>('prompt');
  const [responseSchema, setResponseSchema] = useState<ResponseSchema>({ fields: [] });
  const [contextConfig, setContextConfig] = useState<ContextConfig>({ sections: {}, custom_variables: {} });
  const [schemaDirty, setSchemaDirty] = useState(false);
  const [contextDirty, setContextDirty] = useState(false);
  const [customFieldStats, setCustomFieldStats] = useState<CustomFieldStats | null>(null);
  const [analyticsDays, setAnalyticsDays] = useState(7);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);

  // Comercial
  const [comercialPrompts, setComercialPrompts] = useState<PromptLayerDTO[]>([]);
  const [selectedComercialId, setSelectedComercialId] = useState<string>('');
  const [comercialEdit, setComercialEdit] = useState<EditState>({ content: '', name: '', dirty: false });

  // Threads
  const [threads, setThreads] = useState<ThreadDTO[]>([]);
  const [selectedThreadId, setSelectedThreadId] = useState<string>('');
  const [threadPromptEdit, setThreadPromptEdit] = useState<EditState>({ content: '', name: '', dirty: false });
  const [threadLayers, setThreadLayers] = useState<any>(null);

  // New Thread Modal
  const [showNewThread, setShowNewThread] = useState(false);
  const [newThreadPrompt, setNewThreadPrompt] = useState('');
  const [creatingThread, setCreatingThread] = useState(false);

  // New Comercial Prompt
  const [showNewComercial, setShowNewComercial] = useState(false);
  const [newComercialName, setNewComercialName] = useState('');
  const [newComercialContent, setNewComercialContent] = useState('');
  const [creatingComercial, setCreatingComercial] = useState(false);

  // Auto-clear success
  useEffect(() => {
    if (success) {
      const t = setTimeout(() => setSuccess(null), 4000);
      return () => clearTimeout(t);
    }
  }, [success]);

  // Load data based on tab
  useEffect(() => {
    setError(null);
    setSuccess(null);
    if (activeTab === 'conciencia') loadConciencia();
    else if (activeTab === 'base') loadBase();
    else if (activeTab === 'comercial') loadComercial();
    else if (activeTab === 'threads') loadThreads();
  }, [activeTab]);

  /* ─── Loaders ─── */
  const loadConciencia = async () => {
    setLoading(true);
    try {
      const data = await getConcienciaPrompt();
      setConciencia(data.prompt);
      setConcienciaEdit({ content: data.prompt?.content || '', name: data.prompt?.name || '', dirty: false });
    } catch (e: any) {
      setError(e.message || 'Error cargando prompt conciencia');
    } finally {
      setLoading(false);
    }
  };

  const loadBase = async () => {
    setLoading(true);
    try {
      const data = await getOrgBasePrompt();
      setBase(data.prompt);
      setBaseEdit({ content: data.prompt?.content || '', name: data.prompt?.name || '', dirty: false });
      setResponseSchema(data.prompt?.response_schema || { fields: [] });
      setContextConfig(data.prompt?.context_config || { sections: {}, custom_variables: {} });
      setSchemaDirty(false);
      setContextDirty(false);
    } catch (e: any) {
      setError(e.message || 'Error cargando prompt base');
    } finally {
      setLoading(false);
    }
  };

  const loadComercial = async () => {
    setLoading(true);
    try {
      const data = await listComercialPrompts();
      setComercialPrompts(data.prompts || []);
      if (data.prompts?.length > 0 && !selectedComercialId) {
        const first = data.prompts[0];
        setSelectedComercialId(first.id);
        setComercialEdit({ content: first.content, name: first.name, dirty: false });
      }
    } catch (e: any) {
      setError(e.message || 'Error cargando prompts comerciales');
    } finally {
      setLoading(false);
    }
  };

  const loadThreads = async () => {
    setLoading(true);
    try {
      const data = await getThreads({ limit: 100 });
      setThreads(data || []);
    } catch (e: any) {
      setError(e.message || 'Error cargando threads');
    } finally {
      setLoading(false);
    }
  };

  const loadThreadPrompt = async (threadId: string) => {
    if (!threadId) return;
    setLoading(true);
    try {
      const data = await getThreadPrompts(threadId);
      setThreadLayers(data);
      const threadLayer = data.layers?.find((l: any) => l.type === 'thread');
      setThreadPromptEdit({ content: threadLayer?.content || '', name: '', dirty: false });
    } catch (e: any) {
      setError(e.message || 'Error cargando prompt del thread');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedThreadId) loadThreadPrompt(selectedThreadId);
  }, [selectedThreadId]);

  /* ─── Saves ─── */
  const saveConciencia = async () => {
    setSaving(true); setError(null);
    try {
      await updateConcienciaPrompt(concienciaEdit.content, concienciaEdit.name || undefined);
      setSuccess('Prompt conciencia actualizado');
      setConcienciaEdit(prev => ({ ...prev, dirty: false }));
      await loadConciencia();
    } catch (e: any) {
      setError(e.message || 'Error guardando. ¿Tienes rol CTO/Dev?');
    } finally { setSaving(false); }
  };

  const saveBase = async () => {
    setSaving(true); setError(null);
    try {
      await updateOrgBasePrompt(
        baseEdit.content,
        baseEdit.name || undefined,
        schemaDirty ? responseSchema : undefined,
        contextDirty ? contextConfig : undefined,
      );
      setSuccess('Prompt base actualizado');
      setBaseEdit(prev => ({ ...prev, dirty: false }));
      setSchemaDirty(false);
      setContextDirty(false);
      await loadBase();
    } catch (e: any) {
      setError(e.message || 'Error guardando prompt base');
    } finally { setSaving(false); }
  };

  const saveSchemaOnly = async () => {
    setSaving(true); setError(null);
    try {
      await updateOrgBasePrompt(
        baseEdit.content,
        baseEdit.name || undefined,
        responseSchema,
      );
      setSuccess('Schema de respuesta actualizado');
      setSchemaDirty(false);
      await loadBase();
    } catch (e: any) {
      setError(e.message || 'Error guardando schema');
    } finally { setSaving(false); }
  };

  const saveContextOnly = async () => {
    setSaving(true); setError(null);
    try {
      await updateOrgBasePrompt(
        baseEdit.content,
        baseEdit.name || undefined,
        undefined,
        contextConfig,
      );
      setSuccess('Config de contexto actualizada');
      setContextDirty(false);
      await loadBase();
    } catch (e: any) {
      setError(e.message || 'Error guardando context config');
    } finally { setSaving(false); }
  };

  const loadAnalytics = async () => {
    setLoadingAnalytics(true);
    try {
      const dateTo = new Date();
      const dateFrom = new Date();
      dateFrom.setDate(dateFrom.getDate() - analyticsDays);
      const data = await getCustomFieldAnalytics({
        date_from: dateFrom.toISOString().slice(0, 10),
        date_to: dateTo.toISOString().slice(0, 10),
      });
      setCustomFieldStats(data);
    } catch (e: any) {
      setError(e.message || 'Error cargando analytics');
    } finally { setLoadingAnalytics(false); }
  };

  const saveComercial = async () => {
    setSaving(true); setError(null);
    try {
      const selected = comercialPrompts.find(p => p.id === selectedComercialId);
      await updateComercialPrompt(
        comercialEdit.content,
        comercialEdit.name || undefined,
        selected?.whatsapp_number || undefined
      );
      setSuccess('Prompt comercial actualizado');
      setComercialEdit(prev => ({ ...prev, dirty: false }));
      await loadComercial();
    } catch (e: any) {
      setError(e.message || 'Error guardando prompt comercial');
    } finally { setSaving(false); }
  };

  const saveThreadPrompt = async () => {
    if (!selectedThreadId) return;
    setSaving(true); setError(null);
    try {
      await updateThreadPrompt(selectedThreadId, threadPromptEdit.content);
      setSuccess('Prompt del thread actualizado');
      setThreadPromptEdit(prev => ({ ...prev, dirty: false }));
      await loadThreadPrompt(selectedThreadId);
    } catch (e: any) {
      setError(e.message || 'Error guardando prompt del thread');
    } finally { setSaving(false); }
  };

  const resetThread = async () => {
    if (!selectedThreadId || !confirm('¿Eliminar el prompt personalizado de este thread?')) return;
    setSaving(true); setError(null);
    try {
      await resetThreadPrompt(selectedThreadId);
      setSuccess('Prompt del thread reseteado');
      setThreadPromptEdit({ content: '', name: '', dirty: false });
      await loadThreadPrompt(selectedThreadId);
    } catch (e: any) {
      setError(e.message || 'Error reseteando prompt');
    } finally { setSaving(false); }
  };

  const createNewThread = async () => {
    setCreatingThread(true); setError(null);
    try {
      const result = await startThread({ new_thread: true });
      const threadId = result.thread_id || result.thread?.id;
      if (!threadId) throw new Error('No se pudo crear el thread');

      // Si hay prompt personalizado, asignarlo
      if (newThreadPrompt.trim()) {
        await updateThreadPrompt(threadId, newThreadPrompt.trim());
      }

      setSuccess(`Thread creado (${threadId.substring(0, 8)}…)${newThreadPrompt.trim() ? ' con prompt personalizado' : ''}`);
      setShowNewThread(false);
      setNewThreadPrompt('');

      // Recargar threads y seleccionar el nuevo
      await loadThreads();
      setSelectedThreadId(threadId);
      setActiveTab('threads');
    } catch (e: any) {
      setError(e.message || 'Error creando thread');
    } finally { setCreatingThread(false); }
  };

  const { isAdmin, isDev, userRoles } = useModules();
  const hasDevAccess = isDev || (userRoles && (userRoles.includes('cto') || userRoles.includes('developer')));

  /* ─── Tab config ─── */
  const TABS: { key: PromptTab; label: string; icon: React.ReactNode; desc: string }[] = [
    ...(hasDevAccess ? [{ key: 'conciencia' as PromptTab, label: 'Conciencia', icon: <Brain size={16} />, desc: 'Alma de Orión (CTO)' }] : []),
    { key: 'base',       label: 'Base Org',   icon: <Building2 size={16} />, desc: 'Prompt organizacional' },
    { key: 'comercial',  label: 'Comercial',  icon: <ShoppingCart size={16} />, desc: 'Prompt comercial/WA' },
    { key: 'threads',    label: 'Threads',     icon: <MessageSquare size={16} />, desc: 'Prompt por thread' },
    ...(isAdmin ? [{ key: 'modules' as PromptTab, label: 'Módulos', icon: <Package size={16} />, desc: 'Activar/desactivar módulos' }] : []),
  ];

  /* ─── Render helpers ─── */
  const renderEditor = (
    edit: EditState,
    setEdit: React.Dispatch<React.SetStateAction<EditState>>,
    onSave: () => void,
    placeholder: string,
    extraActions?: React.ReactNode,
    showNameField?: boolean,
  ) => (
    <div className="space-y-3">
      {showNameField && (
        <input
          type="text"
          value={edit.name}
          onChange={e => setEdit(prev => ({ ...prev, name: e.target.value, dirty: true }))}
          placeholder="Nombre del prompt..."
          className="w-full bg-black/30 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50"
        />
      )}
      <textarea
        value={edit.content}
        onChange={e => setEdit(prev => ({ ...prev, content: e.target.value, dirty: true }))}
        placeholder={placeholder}
        className="w-full h-80 bg-black/30 border border-white/20 rounded-lg p-3 text-white text-sm font-mono resize-y focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50"
      />
      <div className="flex items-center justify-between">
        <div className="text-white/40 text-xs">
          {edit.content.length.toLocaleString()} caracteres
          {edit.dirty && <span className="ml-2 text-amber-400">• Sin guardar</span>}
        </div>
        <div className="flex items-center gap-2">
          {extraActions}
          <button
            onClick={onSave}
            disabled={saving || !edit.dirty || !edit.content.trim()}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Save size={14} />
            {saving ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Settings className="text-blue-400" />
            Administración de Prompts
          </h1>
          <p className="text-white/60 mt-1">
            Gestiona la jerarquía completa: conciencia → base → comercial → thread
          </p>
        </div>
        <button
          onClick={() => setShowNewThread(true)}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition-colors"
        >
          <Plus size={16} />
          Nuevo Thread + Prompt
        </button>
      </div>

      {/* Tab selector */}
      <div className="flex gap-1 bg-white/5 p-1 rounded-xl overflow-x-auto">
        {TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === tab.key
                ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                : 'text-white/60 hover:text-white hover:bg-white/10'
            }`}
          >
            {tab.icon}
            <span className="hidden sm:inline">{tab.label}</span>
            <span className="sm:hidden">{tab.label.slice(0, 4)}</span>
          </button>
        ))}
      </div>

      {/* Messages */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-500/20 border border-red-500/30 text-red-400 rounded-lg">
          <AlertCircle size={16} /><span>{error}</span>
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 p-3 bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 rounded-lg">
          <CheckCircle2 size={16} /><span>{success}</span>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="text-center py-8">
          <RotateCw size={24} className="animate-spin mx-auto text-blue-400" />
          <p className="text-white/60 mt-2">Cargando...</p>
        </div>
      )}

      {/* ═══ CONCIENCIA TAB ═══ */}
      {!loading && activeTab === 'conciencia' && hasDevAccess && (
        <div className="bg-white/5 border border-white/10 rounded-xl p-6 space-y-4">
          <div className="flex items-center gap-3">
            <Brain size={24} className="text-purple-400" />
            <div>
              <h2 className="text-white font-semibold text-lg">Prompt Conciencia</h2>
              <p className="text-white/60 text-sm">El alma de Orión. Solo CTO/Dev puede modificar.</p>
            </div>
            <Lock size={16} className="text-amber-400 ml-auto" />
          </div>
          {conciencia && (
            <div className="text-white/50 text-xs flex gap-4">
              <span>v{conciencia.version}</span>
              <span>Actualizado: {conciencia.updated_at ? new Date(conciencia.updated_at).toLocaleString('es-MX') : '—'}</span>
            </div>
          )}
          {renderEditor(concienciaEdit, setConcienciaEdit, saveConciencia, 'Instrucciones fundamentales de Orión...', undefined, true)}
        </div>
      )}

      {/* ═══ BASE TAB ═══ */}
      {!loading && activeTab === 'base' && (
        <div className="bg-white/5 border border-white/10 rounded-xl p-6 space-y-4">
          <div className="flex items-center gap-3">
            <Building2 size={24} className="text-blue-400" />
            <div>
              <h2 className="text-white font-semibold text-lg">Prompt Base (Organización)</h2>
              <p className="text-white/60 text-sm">Instrucciones base, schema de respuesta y variables de contexto.</p>
            </div>
          </div>
          {base && (
            <div className="text-white/50 text-xs flex gap-4">
              <span>v{base.version}</span>
              <span>Actualizado: {base.updated_at ? new Date(base.updated_at).toLocaleString('es-MX') : '—'}</span>
            </div>
          )}

          {/* Sub-tabs */}
          <div className="flex gap-1 bg-black/20 p-1 rounded-lg">
            {([
              { key: 'prompt' as const, label: 'Prompt', icon: <FileText size={13} /> },
              { key: 'schema' as const, label: 'Schema Respuesta', icon: <Database size={13} /> },
              { key: 'context' as const, label: 'Variables Contexto', icon: <Settings size={13} /> },
              { key: 'analytics' as const, label: 'Analytics', icon: <BarChart3 size={13} /> },
            ]).map(st => (
              <button
                key={st.key}
                onClick={() => { setBaseSubTab(st.key); if (st.key === 'analytics' && !customFieldStats) loadAnalytics(); }}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium transition-colors ${
                  baseSubTab === st.key
                    ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                    : 'text-white/50 hover:text-white hover:bg-white/10'
                }`}
              >
                {st.icon}{st.label}
              </button>
            ))}
          </div>

          {/* Sub-tab: Prompt */}
          {baseSubTab === 'prompt' && renderEditor(baseEdit, setBaseEdit, saveBase, 'Instrucciones base de la organización...', undefined, true)}

          {/* Sub-tab: Response Schema */}
          {baseSubTab === 'schema' && (
            <ResponseSchemaEditor
              schema={responseSchema}
              onChange={(s) => { setResponseSchema(s); setSchemaDirty(true); }}
              onSave={saveSchemaOnly}
              saving={saving}
              dirty={schemaDirty}
            />
          )}

          {/* Sub-tab: Context Config */}
          {baseSubTab === 'context' && (
            <ContextConfigEditor
              config={contextConfig}
              onChange={(c) => { setContextConfig(c); setContextDirty(true); }}
              onSave={saveContextOnly}
              saving={saving}
              dirty={contextDirty}
            />
          )}

          {/* Sub-tab: Analytics */}
          {baseSubTab === 'analytics' && (
            <CustomFieldAnalytics
              stats={customFieldStats}
              loading={loadingAnalytics}
              days={analyticsDays}
              onDaysChange={(d) => { setAnalyticsDays(d); }}
              onRefresh={loadAnalytics}
            />
          )}
        </div>
      )}

      {/* ═══ COMERCIAL TAB ═══ */}
      {!loading && activeTab === 'comercial' && (
        <div className="bg-white/5 border border-white/10 rounded-xl p-6 space-y-4">
          <div className="flex items-center gap-3">
            <ShoppingCart size={24} className="text-amber-400" />
            <div>
              <h2 className="text-white font-semibold text-lg">Prompts Comerciales</h2>
              <p className="text-white/60 text-sm">Un prompt activo por número de WhatsApp. Admin/Dev/CTO pueden editar.</p>
            </div>
          </div>

          {/* Selector */}
          {comercialPrompts.length > 0 && (
            <div>
              <label className="block text-white/80 text-sm font-medium mb-2">Seleccionar prompt comercial</label>
              <select
                value={selectedComercialId}
                onChange={e => {
                  const id = e.target.value;
                  setSelectedComercialId(id);
                  const p = comercialPrompts.find(x => x.id === id);
                  if (p) setComercialEdit({ content: p.content, name: p.name, dirty: false });
                }}
                className="w-full bg-black/30 border border-white/20 rounded-lg p-3 text-white focus:ring-2 focus:ring-blue-500/50"
              >
                {comercialPrompts.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.name} {p.whatsapp_number ? `(WA: ${p.whatsapp_number})` : '(default)'}
                    {p.is_active ? ' ✓' : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          {selectedComercialId ? (
            <>
              {(() => {
                const sel = comercialPrompts.find(p => p.id === selectedComercialId);
                return sel && (
                  <div className="text-white/50 text-xs flex gap-4">
                    <span>v{sel.version}</span>
                    {sel.whatsapp_number && <span>WA: {sel.whatsapp_number}</span>}
                    <span>Actualizado: {sel.updated_at ? new Date(sel.updated_at).toLocaleString('es-MX') : '—'}</span>
                  </div>
                );
              })()}
              {renderEditor(comercialEdit, setComercialEdit, saveComercial, 'Instrucciones comerciales para Orión...', undefined, true)}
            </>
          ) : !showNewComercial ? (
            <div className="text-center py-8 bg-black/20 rounded-lg">
              <ShoppingCart size={36} className="mx-auto text-white/20 mb-3" />
              <p className="text-white/60">No hay prompts comerciales configurados.</p>
              <p className="text-white/40 text-sm mt-1 mb-4">Define las instrucciones comerciales que Orión usará con los clientes.</p>
              <button
                onClick={() => setShowNewComercial(true)}
                className="flex items-center gap-2 px-5 py-2.5 bg-amber-500 hover:bg-amber-600 text-white rounded-lg mx-auto transition-colors font-medium"
              >
                <Plus size={16} />
                Crear Prompt Comercial
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              <input
                type="text"
                value={newComercialName}
                onChange={e => setNewComercialName(e.target.value)}
                placeholder="Nombre del prompt (ej: Prompt Comercial Principal)..."
                className="w-full bg-black/30 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500/50"
              />
              <textarea
                value={newComercialContent}
                onChange={e => setNewComercialContent(e.target.value)}
                placeholder="Escribe las instrucciones comerciales para Orión...\n\nEjemplo:\nEres el asistente comercial de [Empresa].\nTu objetivo es atender clientes por WhatsApp, resolver dudas sobre nuestros servicios y guiar hacia la contratación.\n\nServicios y precios:\n- Servicio A: $X\n- Servicio B: $Y\n..."
                className="w-full h-80 bg-black/30 border border-white/20 rounded-lg p-3 text-white text-sm font-mono resize-y focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500/50"
              />
              <div className="flex items-center justify-between">
                <div className="text-white/40 text-xs">
                  {newComercialContent.length.toLocaleString()} caracteres
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => { setShowNewComercial(false); setNewComercialName(''); setNewComercialContent(''); }}
                    className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={async () => {
                      if (!newComercialName.trim() || !newComercialContent.trim()) return;
                      setCreatingComercial(true); setError(null);
                      try {
                        await createComercialPrompt(newComercialContent.trim(), newComercialName.trim());
                        setSuccess('Prompt comercial creado exitosamente');
                        setShowNewComercial(false);
                        setNewComercialName(''); setNewComercialContent('');
                        await loadComercial();
                      } catch (e: any) {
                        setError(e.message || 'Error creando prompt comercial');
                      } finally { setCreatingComercial(false); }
                    }}
                    disabled={creatingComercial || !newComercialName.trim() || newComercialContent.trim().length < 10}
                    className="flex items-center gap-2 px-5 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <Save size={14} />
                    {creatingComercial ? 'Creando...' : 'Crear Prompt Comercial'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ═══ THREADS TAB ═══ */}
      {!loading && activeTab === 'threads' && (
        <div className="bg-white/5 border border-white/10 rounded-xl p-6 space-y-4">
          <div className="flex items-center gap-3">
            <MessageSquare size={24} className="text-emerald-400" />
            <div>
              <h2 className="text-white font-semibold text-lg">Prompt por Thread</h2>
              <p className="text-white/60 text-sm">Personaliza el prompt de cada thread — como GPTs personalizados.</p>
            </div>
            <button
              onClick={() => setShowNewThread(true)}
              className="ml-auto flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 rounded-lg text-sm transition-colors"
            >
              <Plus size={14} /> Nuevo
            </button>
          </div>

          {/* Thread selector */}
          <div>
            <label className="block text-white/80 text-sm font-medium mb-2">Seleccionar Thread</label>
            <select
              value={selectedThreadId}
              onChange={e => {
                if (threadPromptEdit.dirty && !confirm('Tienes cambios sin guardar. ¿Continuar?')) return;
                setSelectedThreadId(e.target.value);
              }}
              className="w-full bg-black/30 border border-white/20 rounded-lg p-3 text-white focus:ring-2 focus:ring-blue-500/50"
            >
              <option value="">Selecciona un thread...</option>
              {threads.map(t => (
                <option key={t.id} value={t.id}>
                  {t.telefono || t.session_id || t.id.substring(0, 12)}
                  {t.status ? ` [${t.status}]` : ''}
                </option>
              ))}
            </select>
          </div>

          {selectedThreadId && (
            <>
              {/* Show inherited layers */}
              {threadLayers?.layers && (
                <div className="space-y-2">
                  {threadLayers.layers
                    .filter((l: any) => l.type !== 'thread')
                    .map((l: any) => (
                      <details key={l.type} className="border border-white/10 rounded-lg overflow-hidden">
                        <summary className="flex items-center gap-2 p-3 bg-white/5 cursor-pointer hover:bg-white/10 text-sm">
                          <Lock size={12} className="text-white/40" />
                          <span className="text-white/70 font-medium capitalize">{l.type}</span>
                          <span className="text-white/40">— {l.name}</span>
                          <span className="ml-auto text-xs text-white/30">{(l.content?.length || 0).toLocaleString()} chars</span>
                        </summary>
                        <pre className="p-3 bg-black/20 text-white/60 text-xs font-mono whitespace-pre-wrap max-h-40 overflow-auto">{l.content}</pre>
                      </details>
                    ))}
                </div>
              )}

              {/* Thread prompt editor */}
              <div className="pt-2">
                <h3 className="text-white font-medium mb-2 flex items-center gap-2">
                  <Edit3 size={14} className="text-blue-400" />
                  Prompt Personalizado del Thread
                </h3>
                {renderEditor(
                  threadPromptEdit,
                  setThreadPromptEdit,
                  saveThreadPrompt,
                  'Instrucciones personalizadas para este thread... (como un GPT personalizado)',
                  <button
                    onClick={resetThread}
                    disabled={saving}
                    className="flex items-center gap-2 px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg disabled:opacity-50 transition-colors"
                  >
                    <Trash2 size={14} /> Resetear
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* Help */}
      <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
        <h4 className="text-blue-400 font-medium flex items-center gap-2">
          <Sparkles size={16} />
          Jerarquía de Prompts
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
          <div className="text-sm">
            <div className="text-purple-400 font-medium">🧠 Conciencia</div>
            <div className="text-white/50">Alma de Orión — solo CTO/Dev</div>
          </div>
          <div className="text-sm">
            <div className="text-blue-400 font-medium">🏢 Base Org</div>
            <div className="text-white/50">Por organización — Admin/Dev/CTO</div>
          </div>
          <div className="text-sm">
            <div className="text-amber-400 font-medium">🛒 Comercial</div>
            <div className="text-white/50">Por nro. WhatsApp — Admin/Dev/CTO</div>
          </div>
          <div className="text-sm">
            <div className="text-emerald-400 font-medium">💬 Thread</div>
            <div className="text-white/50">Por conversación — Admin/Dev (GPT-like)</div>
          </div>
        </div>
        <p className="text-white/40 text-xs mt-3">
          Los prompts se combinan en orden: conciencia → base → comercial → thread. El resultado final es lo que usa Orión.
        </p>
      </div>

      {/* ═══ NEW THREAD MODAL ═══ */}
      {showNewThread && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setShowNewThread(false)}>
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <div className="relative bg-[#1a1a2e] border border-white/20 rounded-2xl w-full max-w-xl shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="p-6 space-y-4">
              {/* Modal header */}
              <div className="flex items-center justify-between">
                <h2 className="text-white font-bold text-lg flex items-center gap-2">
                  <Sparkles size={20} className="text-emerald-400" />
                  Crear Nuevo Thread con Prompt
                </h2>
                <button onClick={() => setShowNewThread(false)} className="text-white/60 hover:text-white">
                  <X size={20} />
                </button>
              </div>

              <p className="text-white/60 text-sm">
                Crea una nueva conversación con instrucciones personalizadas — como un GPT personalizado o una Gema de Google.
              </p>

              <div>
                <label className="block text-white/80 text-sm font-medium mb-2">
                  Prompt personalizado (opcional)
                </label>
                <textarea
                  value={newThreadPrompt}
                  onChange={e => setNewThreadPrompt(e.target.value)}
                  placeholder="Ej: Eres un experto en marketing digital. Genera ideas creativas para campañas en redes sociales..."
                  className="w-full h-48 bg-black/30 border border-white/20 rounded-lg p-3 text-white text-sm font-mono resize-y focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50"
                />
                <div className="text-white/40 text-xs mt-1">
                  {newThreadPrompt.length.toLocaleString()} caracteres
                  {!newThreadPrompt.trim() && ' — Se usarán los prompts base del sistema'}
                </div>
              </div>

              {/* Suggested prompts */}
              <div>
                <p className="text-white/50 text-xs mb-2">Sugerencias rápidas:</p>
                <div className="flex flex-wrap gap-2">
                  {[
                    { label: '📊 Analista de datos', prompt: 'Eres un analista de datos experto. Ayuda a interpretar métricas, crear reportes y encontrar insights en los datos del negocio.' },
                    { label: '✍️ Copywriter', prompt: 'Eres un copywriter creativo especializado en marketing digital. Genera textos persuasivos, catchy y optimizados para conversión.' },
                    { label: '💼 Asesor de ventas', prompt: 'Eres un asesor comercial experto. Ayuda a calificar leads, preparar propuestas y cerrar ventas con técnicas consultivas.' },
                    { label: '🎨 Diseñador UX', prompt: 'Eres un experto en UX/UI. Ayuda a diseñar flujos de usuario, mejorar interfaces y crear wireframes conceptuales.' },
                  ].map(s => (
                    <button
                      key={s.label}
                      onClick={() => setNewThreadPrompt(s.prompt)}
                      className="px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white/70 hover:text-white rounded-lg text-xs transition-colors"
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  onClick={() => setShowNewThread(false)}
                  className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={createNewThread}
                  disabled={creatingThread}
                  className="flex items-center gap-2 px-5 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg disabled:opacity-50 transition-colors"
                >
                  {creatingThread ? <RotateCw size={14} className="animate-spin" /> : <Plus size={14} />}
                  {creatingThread ? 'Creando...' : 'Crear Thread'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══ MODULES TAB ═══ */}
      {!loading && activeTab === 'modules' && (
        <ModuleAdmin />
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   RESPONSE SCHEMA EDITOR
   Define los campos extra que el LLM debe devolver en su JSON.
   ═══════════════════════════════════════════════════════════════ */

const FIELD_TYPES: ResponseSchemaField['type'][] = ['string', 'number', 'boolean', 'array'];

function ResponseSchemaEditor({
  schema, onChange, onSave, saving, dirty,
}: {
  schema: ResponseSchema;
  onChange: (s: ResponseSchema) => void;
  onSave: () => void;
  saving: boolean;
  dirty: boolean;
}) {
  const [newField, setNewField] = useState<ResponseSchemaField>({ key: '', type: 'string', label: '', description: '', required: false });

  const fields = schema.fields || [];

  const addField = () => {
    const key = newField.key.trim().toLowerCase().replace(/[^a-z0-9_]/g, '_');
    if (!key || fields.some(f => f.key === key)) return;
    onChange({
      ...schema,
      fields: [...fields, { ...newField, key }],
    });
    setNewField({ key: '', type: 'string', label: '', description: '', required: false });
  };

  const removeField = (key: string) => {
    onChange({ ...schema, fields: fields.filter(f => f.key !== key) });
  };

  const updateField = (key: string, patch: Partial<ResponseSchemaField>) => {
    onChange({
      ...schema,
      fields: fields.map(f => f.key === key ? { ...f, ...patch } : f),
    });
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-white font-medium flex items-center gap-2 mb-1">
          <Database size={16} className="text-blue-400" />
          Campos Custom del LLM
        </h3>
        <p className="text-white/40 text-xs">
          Define campos adicionales que Orión debe incluir en cada respuesta JSON. Estos se guardan en <code className="text-white/60">extra_fields</code> de ConversationLog y habilitan dashboards dinámicos.
        </p>
      </div>

      {/* Existing fields */}
      {fields.length > 0 ? (
        <div className="space-y-2">
          {fields.map(field => (
            <div key={field.key} className="border border-white/10 rounded-lg p-3 bg-black/20 space-y-2">
              <div className="flex items-center gap-2">
                <code className="text-blue-300 text-sm font-mono font-semibold">"{field.key}"</code>
                <span className="text-white/40 text-xs">({field.type})</span>
                {field.label && <span className="text-white/50 text-xs">— {field.label}</span>}
                {field.required && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400 border border-amber-500/30 font-medium">OBLIGATORIO</span>}
                {!field.required && <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-white/30 border border-white/10">opcional</span>}
                <span className="flex-1" />
                <button onClick={() => removeField(field.key)} className="p-1 rounded text-red-400 hover:bg-red-500/20 transition-colors"><Trash2 size={13} /></button>
              </div>
              <div className="flex gap-2 items-center">
                <input
                  value={field.label}
                  onChange={e => updateField(field.key, { label: e.target.value })}
                  placeholder="Etiqueta para UI…"
                  className="flex-1 bg-black/30 border border-white/15 rounded px-2.5 py-1.5 text-white text-xs focus:ring-1 focus:ring-blue-500/50"
                />
                <select
                  value={field.type}
                  onChange={e => updateField(field.key, { type: e.target.value as ResponseSchemaField['type'] })}
                  className="bg-black/30 border border-white/15 rounded px-2 py-1 text-white text-xs"
                >
                  {FIELD_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <input
                value={field.description}
                onChange={e => updateField(field.key, { description: e.target.value })}
                placeholder="Descripción para el LLM…"
                className="w-full bg-black/30 border border-white/15 rounded px-2.5 py-1.5 text-white text-xs focus:ring-1 focus:ring-blue-500/50"
              />
              <label className="flex items-center gap-1.5 text-white/50 text-xs cursor-pointer select-none">
                <input type="checkbox" checked={field.required} onChange={e => updateField(field.key, { required: e.target.checked })} className="accent-blue-500" />
                Requerido
              </label>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-6 bg-black/20 rounded-lg border border-dashed border-white/10">
          <Database size={28} className="mx-auto text-white/15 mb-2" />
          <p className="text-white/40 text-sm">Sin campos custom configurados.</p>
          <p className="text-white/25 text-xs mt-1">Agrega campos para que el LLM los incluya en cada respuesta.</p>
        </div>
      )}

      {/* Add new field */}
      <div className="border border-dashed border-white/15 rounded-lg p-3 space-y-2">
        <p className="text-[10px] uppercase tracking-widest text-white/25 font-semibold">Agregar campo</p>
        <div className="flex gap-2 items-end flex-wrap">
          <div className="min-w-[120px]">
            <label className="text-white/50 text-[10px] block mb-0.5">Key</label>
            <input
              value={newField.key}
              onChange={e => setNewField(prev => ({ ...prev, key: e.target.value }))}
              placeholder="lead_score"
              className="w-full bg-black/30 border border-white/15 rounded px-2.5 py-1.5 text-white text-xs font-mono focus:ring-1 focus:ring-blue-500/50"
            />
          </div>
          <div className="min-w-[120px]">
            <label className="text-white/50 text-[10px] block mb-0.5">Label</label>
            <input
              value={newField.label}
              onChange={e => setNewField(prev => ({ ...prev, label: e.target.value }))}
              placeholder="Puntuación del lead"
              className="w-full bg-black/30 border border-white/15 rounded px-2.5 py-1.5 text-white text-xs focus:ring-1 focus:ring-blue-500/50"
            />
          </div>
          <div>
            <label className="text-white/50 text-[10px] block mb-0.5">Tipo</label>
            <select
              value={newField.type}
              onChange={e => setNewField(prev => ({ ...prev, type: e.target.value as ResponseSchemaField['type'] }))}
              className="bg-black/30 border border-white/15 rounded px-2 py-1.5 text-white text-xs"
            >
              {FIELD_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="text-white/50 text-[10px] block mb-0.5">Descripción</label>
            <input
              value={newField.description}
              onChange={e => setNewField(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Qué debe poner el LLM aquí…"
              className="w-full bg-black/30 border border-white/15 rounded px-2.5 py-1.5 text-white text-xs focus:ring-1 focus:ring-blue-500/50"
            />
          </div>
          <label className="flex items-center gap-1.5 text-white/50 text-xs cursor-pointer select-none pb-0.5">
            <input type="checkbox" checked={newField.required} onChange={e => setNewField(prev => ({ ...prev, required: e.target.checked }))} className="accent-blue-500" />
            Req
          </label>
          <button
            onClick={addField}
            disabled={!newField.key.trim()}
            className="flex items-center gap-1 px-3 py-1.5 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg text-xs font-medium disabled:opacity-30 transition-colors"
          >
            <Plus size={13} /> Agregar
          </button>
        </div>
      </div>

      {/* Preview */}
      {fields.length > 0 && (
        <details className="border border-white/10 rounded-lg overflow-hidden">
          <summary className="p-3 bg-white/5 cursor-pointer hover:bg-white/10 text-sm text-white/60 flex items-center gap-2">
            <Eye size={14} /> Vista previa: instrucción que se inyecta al LLM
          </summary>
          <pre className="p-3 bg-black/20 text-emerald-300/80 text-xs font-mono whitespace-pre-wrap max-h-60 overflow-auto">
{`### CAMPOS ADICIONALES EN TU JSON DE RESPUESTA
Además de los campos estándar (bloques, sentimiento, keywords, tool_calls),
DEBES incluir estos campos en tu respuesta JSON:

${fields.map(f => {
  const req = f.required ? 'OBLIGATORIO' : 'opcional';
  return `- "${f.key}" (${f.type}, ${req}): ${f.description}${f.label ? ` [${f.label}]` : ''}`;
}).join('\n')}`}
          </pre>
        </details>
      )}

      {/* Save */}
      <div className="flex items-center justify-between">
        <div className="text-white/40 text-xs">
          {fields.length} campo{fields.length !== 1 ? 's' : ''} definido{fields.length !== 1 ? 's' : ''}
          {dirty && <span className="ml-2 text-amber-400">• Sin guardar</span>}
        </div>
        <button
          onClick={onSave}
          disabled={saving || !dirty}
          className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
        >
          <Save size={14} />
          {saving ? 'Guardando...' : 'Guardar Schema'}
        </button>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   CONTEXT CONFIG EDITOR
   Toggles de secciones de contexto + variables custom estáticas.
   ═══════════════════════════════════════════════════════════════ */

const CONTEXT_SECTIONS: { key: string; label: string; desc: string }[] = [
  { key: 'trips', label: 'Viajes activos', desc: 'Trips activos con nombre, precio, fechas' },
  { key: 'pricing', label: 'Precios y planes', desc: 'Planes y addons con precios vigentes' },
  { key: 'company_info', label: 'Info de la empresa', desc: 'Datos de la empresa, contacto, slogan' },
  { key: 'faqs', label: 'FAQs', desc: 'Preguntas frecuentes por viaje' },
  { key: 'blogs', label: 'Blog / Artículos', desc: 'Artículos publicados para recomendar' },
];

function ContextConfigEditor({
  config, onChange, onSave, saving, dirty,
}: {
  config: ContextConfig;
  onChange: (c: ContextConfig) => void;
  onSave: () => void;
  saving: boolean;
  dirty: boolean;
}) {
  const [newVarKey, setNewVarKey] = useState('');
  const [newVarVal, setNewVarVal] = useState('');

  const sections = config.sections || {};
  const customCtx = config.custom_variables || {};

  const toggleSection = (key: string) => {
    const current = sections[key] ?? true; // default true = backward compat
    onChange({ ...config, sections: { ...sections, [key]: !current } });
  };

  const addCustomVar = () => {
    const key = newVarKey.trim();
    if (!key) return;
    onChange({ ...config, custom_variables: { ...customCtx, [key]: newVarVal } });
    setNewVarKey('');
    setNewVarVal('');
  };

  const removeCustomVar = (key: string) => {
    const copy = { ...customCtx };
    delete copy[key];
    onChange({ ...config, custom_variables: copy });
  };

  const updateCustomVar = (key: string, val: string) => {
    onChange({ ...config, custom_variables: { ...customCtx, [key]: val } });
  };

  return (
    <div className="space-y-4">
      {/* Section toggles */}
      <div>
        <h3 className="text-white font-medium flex items-center gap-2 mb-1">
          <Settings size={16} className="text-blue-400" />
          Secciones de Contexto
        </h3>
        <p className="text-white/40 text-xs mb-3">
          Activa o desactiva las secciones de contexto que se inyectan en el prompt del LLM. Por defecto todas están activas.
        </p>
        <div className="space-y-1">
          {CONTEXT_SECTIONS.map(s => {
            const enabled = sections[s.key] ?? true;
            return (
              <button
                key={s.key}
                onClick={() => toggleSection(s.key)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-left ${
                  enabled ? 'bg-blue-500/10 border border-blue-500/20' : 'bg-black/20 border border-white/5'
                }`}
              >
                {enabled
                  ? <ToggleRight size={18} className="text-blue-400 shrink-0" />
                  : <ToggleLeft size={18} className="text-white/20 shrink-0" />
                }
                <div className="min-w-0">
                  <div className={`text-sm font-medium ${enabled ? 'text-white' : 'text-white/40'}`}>{s.label}</div>
                  <div className="text-[10px] text-white/30">{s.desc}</div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Custom context variables */}
      <div>
        <h3 className="text-white font-medium flex items-center gap-2 mb-1">
          Variables Custom
        </h3>
        <p className="text-white/40 text-xs mb-3">
          Variables estáticas que se inyectan como sección extra en el prompt. Útil para info de la org que no cambia frecuentemente.
        </p>

        {Object.entries(customCtx).length > 0 ? (
          <div className="space-y-2 mb-3">
            {Object.entries(customCtx).map(([key, val]) => (
              <div key={key} className="flex items-start gap-2 border border-white/10 rounded-lg p-2.5 bg-black/20">
                <code className="text-blue-300 text-xs font-mono mt-0.5 shrink-0">{key}:</code>
                <input
                  value={val}
                  onChange={e => updateCustomVar(key, e.target.value)}
                  className="flex-1 bg-black/30 border border-white/15 rounded px-2 py-1 text-white text-xs focus:ring-1 focus:ring-blue-500/50"
                />
                <button onClick={() => removeCustomVar(key)} className="p-1 rounded text-red-400 hover:bg-red-500/20 transition-colors shrink-0">
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-4 bg-black/20 rounded-lg border border-dashed border-white/10 mb-3">
            <p className="text-white/30 text-xs">Sin variables custom.</p>
          </div>
        )}

        <div className="flex gap-2 items-end">
          <div className="min-w-[140px]">
            <label className="text-white/50 text-[10px] block mb-0.5">Clave</label>
            <input
              value={newVarKey}
              onChange={e => setNewVarKey(e.target.value)}
              placeholder="horario_atencion"
              className="w-full bg-black/30 border border-white/15 rounded px-2.5 py-1.5 text-white text-xs font-mono focus:ring-1 focus:ring-blue-500/50"
            />
          </div>
          <div className="flex-1">
            <label className="text-white/50 text-[10px] block mb-0.5">Valor</label>
            <input
              value={newVarVal}
              onChange={e => setNewVarVal(e.target.value)}
              placeholder="Lunes a Viernes 9am-6pm"
              className="w-full bg-black/30 border border-white/15 rounded px-2.5 py-1.5 text-white text-xs focus:ring-1 focus:ring-blue-500/50"
            />
          </div>
          <button
            onClick={addCustomVar}
            disabled={!newVarKey.trim()}
            className="flex items-center gap-1 px-3 py-1.5 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg text-xs font-medium disabled:opacity-30 transition-colors"
          >
            <Plus size={13} /> Agregar
          </button>
        </div>
      </div>

      {/* Save */}
      <div className="flex items-center justify-between pt-2">
        <div className="text-white/40 text-xs">
          {Object.keys(sections).filter(k => !(sections[k] ?? true)).length} sección(es) desactivada(s) · {Object.keys(customCtx).length} variable(s) custom
          {dirty && <span className="ml-2 text-amber-400">• Sin guardar</span>}
        </div>
        <button
          onClick={onSave}
          disabled={saving || !dirty}
          className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
        >
          <Save size={14} />
          {saving ? 'Guardando...' : 'Guardar Contexto'}
        </button>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   CUSTOM FIELD ANALYTICS
   Dashboard dinámico basado en extra_fields de ConversationLog.
   ═══════════════════════════════════════════════════════════════ */

function CustomFieldAnalytics({
  stats, loading, days, onDaysChange, onRefresh,
}: {
  stats: CustomFieldStats | null;
  loading: boolean;
  days: number;
  onDaysChange: (d: number) => void;
  onRefresh: () => void;
}) {
  if (loading) {
    return (
      <div className="text-center py-8">
        <RotateCw size={20} className="animate-spin mx-auto text-blue-400" />
        <p className="text-white/50 text-sm mt-2">Cargando analytics de campos custom…</p>
      </div>
    );
  }

  const fieldEntries = stats ? Object.entries(stats.fields_summary) : [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-white font-medium flex items-center gap-2">
            <BarChart3 size={16} className="text-blue-400" />
            Analytics de Campos Custom
          </h3>
          <p className="text-white/40 text-xs mt-0.5">Datos extraídos por el LLM de las conversaciones.</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={days}
            onChange={e => { onDaysChange(+e.target.value); }}
            className="bg-black/30 border border-white/15 rounded px-2 py-1 text-white text-xs"
          >
            {[1, 3, 7, 14, 30].map(d => <option key={d} value={d}>Últimos {d} día{d > 1 ? 's' : ''}</option>)}
          </select>
          <button onClick={onRefresh} className="flex items-center gap-1 px-2.5 py-1 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded text-xs transition-colors">
            <RotateCw size={12} /> Actualizar
          </button>
        </div>
      </div>

      {/* Period & total */}
      {stats && (
        <div className="flex items-center gap-4 text-xs text-white/50">
          <span>Periodo: {stats.period.from} — {stats.period.to}</span>
          <span>Total conversaciones: <span className="text-white font-semibold">{stats.total_conversations}</span></span>
        </div>
      )}

      {fieldEntries.length === 0 ? (
        <div className="text-center py-8 bg-black/20 rounded-lg border border-dashed border-white/10">
          <BarChart3 size={32} className="mx-auto text-white/15 mb-2" />
          <p className="text-white/40 text-sm">Sin datos de campos custom aún.</p>
          <p className="text-white/25 text-xs mt-1">
            Configura campos en la pestaña "Schema Respuesta" y los datos aparecerán aquí conforme el LLM procese conversaciones.
          </p>
        </div>
      ) : (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {fieldEntries.map(([name, summary]) => {
              let display = '—';
              if (summary.type === 'number' && summary.avg != null) display = summary.avg.toFixed(1);
              else if (summary.type === 'string' && summary.top_values?.length) display = summary.top_values[0].value;
              else if (summary.type === 'boolean' && summary.top_values?.length) {
                const trueVal = summary.top_values.find(v => v.value === 'true');
                display = trueVal ? `${((trueVal.count / summary.count) * 100).toFixed(0)}% true` : '—';
              }
              return (
                <div key={name} className="bg-black/20 border border-white/10 rounded-lg p-3">
                  <code className="text-blue-300 text-[10px] font-mono">{name}</code>
                  <div className="text-white text-lg font-bold mt-1 truncate" title={display}>{display}</div>
                  <div className="text-white/30 text-[10px] mt-0.5">{summary.count} registro{summary.count !== 1 ? 's' : ''} · {summary.type}</div>
                </div>
              );
            })}
          </div>

          {/* Distributions for string fields */}
          {fieldEntries.map(([name, summary]) => {
            if (summary.type !== 'string' || !summary.top_values?.length) return null;
            return (
              <div key={`dist-${name}`} className="bg-black/20 border border-white/10 rounded-lg p-4">
                <h4 className="text-white text-sm font-medium mb-3">
                  Distribución: <code className="text-blue-300 font-mono">{name}</code>
                </h4>
                <div className="space-y-1.5">
                  {summary.top_values.slice(0, 10).map(({ value, count }) => {
                    const pct = summary.count > 0 ? ((count / summary.count) * 100).toFixed(0) : '0';
                    return (
                      <div key={value} className="flex items-center gap-2">
                        <span className="text-white/70 text-xs w-28 truncate" title={value}>{value}</span>
                        <div className="flex-1 h-4 bg-white/5 rounded overflow-hidden">
                          <div
                            className="h-full bg-blue-500/40 rounded transition-all"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className="text-white/40 text-[10px] w-12 text-right">{pct}%</span>
                        <span className="text-white/25 text-[10px] w-8 text-right">{count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* Numeric fields detail */}
          {fieldEntries.map(([name, summary]) => {
            if (summary.type !== 'number') return null;
            return (
              <div key={`num-${name}`} className="bg-black/20 border border-white/10 rounded-lg p-4">
                <h4 className="text-white text-sm font-medium mb-2">
                  Numérico: <code className="text-blue-300 font-mono">{name}</code>
                </h4>
                <div className="flex gap-6 text-xs">
                  <div><span className="text-white/40">Promedio:</span> <span className="text-white font-semibold">{summary.avg?.toFixed(1) ?? '—'}</span></div>
                  <div><span className="text-white/40">Mín:</span> <span className="text-white">{summary.min ?? '—'}</span></div>
                  <div><span className="text-white/40">Máx:</span> <span className="text-white">{summary.max ?? '—'}</span></div>
                  <div><span className="text-white/40">N:</span> <span className="text-white">{summary.count}</span></div>
                </div>
                {summary.distribution && summary.distribution.length > 0 && (
                  <div className="mt-3 space-y-1">
                    {summary.distribution.map(({ range, count }) => {
                      const pct = summary.count > 0 ? ((count / summary.count) * 100).toFixed(0) : '0';
                      return (
                        <div key={range} className="flex items-center gap-2">
                          <span className="text-white/70 text-xs w-20">{range}</span>
                          <div className="flex-1 h-3 bg-white/5 rounded overflow-hidden">
                            <div className="h-full bg-emerald-500/40 rounded transition-all" style={{ width: `${pct}%` }} />
                          </div>
                          <span className="text-white/40 text-[10px] w-8 text-right">{count}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}

export default PromptAdmin;
